import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import DayEntryModal from "@/components/DayEntryModal";
import { api, formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

function daysInMonth(y, m) {
  return new Date(y, m + 1, 0).getDate();
}

function firstWeekdayMon(y, m) {
  // Monday-indexed 0..6
  const d = new Date(y, m, 1).getDay(); // Sun=0..Sat=6
  return (d + 6) % 7;
}

function MonthGrid({ year, month, entryMap, onDayClick, currency }) {
  const days = daysInMonth(year, month);
  const startPad = firstWeekdayMon(year, month);
  const today = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(<div key={`p${i}`} />);
  for (let d = 1; d <= days; d++) {
    const date = fmtDate(year, month, d);
    const entry = entryMap[date];
    const noPos = entry?.no_positions;
    const pnl = entry?.total_pnl;
    const hasEntry = !!entry;
    const isProfit = hasEntry && !noPos && pnl > 0;
    const isLoss = hasEntry && !noPos && pnl < 0;
    const isFlat = hasEntry && !noPos && pnl === 0 && (entry?.trades?.length ?? 0) > 0;
    const isToday = date === today;

    let borderClass = "border-[#2E2A25]";
    let bgClass = "bg-[#0F0E0C]";
    if (noPos) {
      borderClass = "border-[#5C7C8A]/40";
      bgClass = "bg-[#5C7C8A]/5";
    } else if (isProfit) {
      borderClass = "border-[#5E7A62]/60";
      bgClass = "bg-[#5E7A62]/10";
    } else if (isLoss) {
      borderClass = "border-[#8C4A42]/60";
      bgClass = "bg-[#8C4A42]/10";
    } else if (isFlat) {
      borderClass = "border-[#5C7C8A]/50";
    }

    cells.push(
      <button
        key={date}
        data-testid={TID.dayCell(date)}
        onClick={() => onDayClick(date)}
        className={`relative aspect-square border ${borderClass} ${bgClass} hover:border-[#CFA66A] hover:bg-[#26231F] transition-all p-1.5 flex flex-col justify-between text-left rounded-sm group`}
      >
        <div className="flex items-center justify-between">
          <span
            className={`font-mono text-[11px] ${
              isToday ? "text-[#CFA66A] font-semibold" : "text-[#A39A8E]"
            }`}
          >
            {pad(d)}
          </span>
          {isToday && <span className="w-1 h-1 rounded-full bg-[#CFA66A]" />}
        </div>
        {noPos ? (
          <div className="flex items-center gap-1">
            <span className="font-mono text-[9px] text-[#5C7C8A]">&#10003;</span>
            <span className="font-mono text-[8px] text-[#5C7C8A]/80">no pos</span>
          </div>
        ) : hasEntry ? (
          <div className="flex flex-col items-start">
            <span
              className={`font-mono text-[10px] leading-tight ${
                isProfit ? "text-[#7A9E80]" : isLoss ? "text-[#B56258]" : "text-[#A39A8E]"
              }`}
            >
              {formatMoney(pnl, currency).replace(/\.\d{2}$/, "")}
            </span>
            {entry.trades?.length > 0 && (
              <span className="font-mono text-[8px] text-[#A39A8E]/70">
                {entry.trades.length}T
              </span>
            )}
          </div>
        ) : (
          <span className="font-mono text-[8px] text-[#A39A8E]/40 opacity-0 group-hover:opacity-100 transition-opacity">
            no pos
          </span>
        )}
      </button>
    );
  }

  return (
    <div id={`month-${month}`} className="border border-[#2E2A25] bg-[#1A1815] p-4 rounded-sm scroll-mt-24">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-head text-lg text-[#EBE5D9]">{MONTHS[month]}</h3>
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
          {pad(month + 1)}/{year}
        </span>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <div
            key={i}
            className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]/60 text-center py-1"
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">{cells}</div>
    </div>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/journal/entries?year=${year}`);
      setEntries(data);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Scroll to current month once entries have loaded (only if viewing the current year)
  useEffect(() => {
    if (loading) return;
    const now = new Date();
    if (year !== now.getFullYear()) return;
    const el = document.getElementById(`month-${now.getMonth()}`);
    if (el) {
      // Use rAF so the browser has painted the grid before we scroll
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [loading, year]);

  const entryMap = useMemo(() => {
    const m = {};
    for (const e of entries) m[e.date] = e;
    return m;
  }, [entries]);

  const yearTotal = useMemo(
    () => entries.reduce((s, e) => s + (e.total_pnl || 0), 0),
    [entries]
  );

  const tradingDays = entries.length;
  const winDays = entries.filter((e) => e.total_pnl > 0).length;

  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!q) return null;
    const matches = [];
    for (const e of entries) {
      const text = [
        e.setup_strategy, e.entry_exit_reasoning, e.emotions, e.mistakes, e.lessons,
      ].join(" ").toLowerCase();
      const tradeHits = (e.trades || []).filter(
        (t) => (t.pair || "").toLowerCase().includes(q) || (t.strategy || "").toLowerCase().includes(q)
      );
      if (text.includes(q) || tradeHits.length > 0) {
        matches.push({ entry: e, tradeHits });
      }
    }
    // Newest first
    matches.sort((a, b) => (a.entry.date < b.entry.date ? 1 : -1));
    return matches;
  }, [entries, q]);

  return (
    <Layout year={year} setYear={setYear}>
      <div className="mb-8 fade-in">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">
          &mdash; {year} Trading Year
        </p>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <h1 className="font-head text-4xl sm:text-5xl font-bold text-[#EBE5D9]">
            Annual Ledger
          </h1>
          <div className="flex items-center gap-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                YTD P&amp;L
              </p>
              <p
                className={`font-mono text-2xl font-medium ${
                  yearTotal > 0
                    ? "text-[#7A9E80]"
                    : yearTotal < 0
                      ? "text-[#B56258]"
                      : "text-[#EBE5D9]"
                }`}
              >
                {formatMoney(yearTotal, user?.currency)}
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                Days Journaled
              </p>
              <p className="font-mono text-2xl font-medium text-[#EBE5D9]">
                {tradingDays}
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                Green Days
              </p>
              <p className="font-mono text-2xl font-medium text-[#7A9E80]">
                {winDays}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col md:flex-row md:items-center gap-3">
          <input
            data-testid="calendar-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search entries — pair, strategy, or a word from your reflections"
            className="flex-1 bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] placeholder:text-[#A39A8E]/60 px-4 py-2.5 rounded-sm focus:outline-none focus:border-[#CFA66A] transition-colors font-mono text-sm"
          />
          {q && (
            <button
              data-testid="calendar-search-clear"
              onClick={() => setQuery("")}
              className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E] hover:text-[#EBE5D9] transition-colors px-2"
            >
              Clear
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs font-mono text-[#A39A8E]">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 border border-[#5E7A62]/60 bg-[#5E7A62]/10" />
            profit
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 border border-[#8C4A42]/60 bg-[#8C4A42]/10" />
            loss
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 border border-[#5C7C8A]/50" />
            flat
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 border border-[#5C7C8A]/40 bg-[#5C7C8A]/5 flex items-center justify-center">
              <span className="font-mono text-[7px] text-[#5C7C8A]">&#10003;</span>
            </span>
            no positions
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 border border-[#2E2A25]" />
            unfilled
          </span>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-[#A39A8E] py-16 font-mono text-sm">
          Loading ledger...
        </div>
      ) : searchResults ? (
        <div data-testid="calendar-search-results" className="space-y-3">
          <p className="font-mono text-xs text-[#A39A8E] uppercase tracking-widest">
            {searchResults.length} match{searchResults.length === 1 ? "" : "es"} for &ldquo;{query}&rdquo;
          </p>
          {searchResults.length === 0 && (
            <div className="border border-dashed border-[#2E2A25] p-6 text-center rounded-sm font-mono text-sm text-[#A39A8E]">
              Nothing found. Try a shorter word or a pair like EURUSD.
            </div>
          )}
          {searchResults.map(({ entry, tradeHits }) => (
            <button
              key={entry.date}
              data-testid={`calendar-search-hit-${entry.date}`}
              onClick={() => setSelectedDate(entry.date)}
              className="w-full text-left border border-[#2E2A25] bg-[#1A1815] p-4 rounded-sm hover:border-[#CFA66A] transition-colors"
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="font-mono text-sm text-[#EBE5D9]">{entry.date}</p>
                <p className={`font-mono text-sm ${
                  entry.total_pnl > 0 ? "text-[#7A9E80]" : entry.total_pnl < 0 ? "text-[#B56258]" : "text-[#A39A8E]"
                }`}>
                  {formatMoney(entry.total_pnl, user?.currency)}
                </p>
              </div>
              {tradeHits.length > 0 && (
                <p className="font-mono text-[10px] text-[#CFA66A] uppercase tracking-widest mt-1">
                  {tradeHits.map((t) => `${t.pair}${t.strategy ? " · " + t.strategy : ""}`).join(" • ")}
                </p>
              )}
              {entry.lessons && (
                <p className="text-xs text-[#A39A8E] mt-2 line-clamp-2 whitespace-pre-wrap">
                  {entry.lessons}
                </p>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div
          data-testid={TID.calendarGrid}
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6"
        >
          {MONTHS.map((_, i) => (
            <MonthGrid
              key={i}
              year={year}
              month={i}
              entryMap={entryMap}
              currency={user?.currency}
              onDayClick={setSelectedDate}
            />
          ))}
        </div>
      )}

      {selectedDate && (
        <DayEntryModal
          date={selectedDate}
          onClose={() => setSelectedDate(null)}
          onSaved={loadEntries}
        />
      )}
    </Layout>
  );
}
